import '../styles/People.scss';

import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { setBackgroundImage } from '../helpers/helpers';
import { Gender, PersonModel } from '../models';

import InfoList from '../components/InfoList';
import Loader from '../components/Loader';
import MediaList from '../components/MediaList';

const DROPDOWN_JOBS = ['Actor', 'Director', 'Writer', 'Producer'];

function getCredits(personDetails: PersonModel, type?: string) {
    let cast = personDetails.combined_credits?.cast || [];
    let crew = personDetails.combined_credits?.crew || [];

    let credits = cast.concat(crew);

    if (type === 'cast') {
        credits = cast;
    } else if (type === 'crew') {
        credits = crew;
    }

    credits = credits.filter((credit, index, self) => {
        if (type === 'crew') {
            return index === self.findIndex((c) => c.id === credit.id && c.job === credit.job);
        } else {
            return index === self.findIndex((c) => c.id === credit.id);
        }
    });

    credits = credits.sort((a, b) => {
        const dateA = new Date(a.release_date || a.first_air_date || 0).getTime();
        const dateB = new Date(b.release_date || b.first_air_date || 0).getTime();
        return dateB - dateA;
    });

    return credits;
}

const mapDepartmentToJob = (department: string): string => {
    const mapping: Record<string, string> = {
        Acting: 'Actor',
        Directing: 'Director',
        Writing: 'Writer',
        Production: 'Producer',
    };
    return mapping[department] || department;
};

const extractJobOptions = (personDetails: PersonModel): string[] => {
    const jobs = new Set<string>();

    if (personDetails.combined_credits?.cast?.length && DROPDOWN_JOBS.includes('Actor')) {
        jobs.add('Actor');
    }

    if (personDetails.combined_credits?.crew?.length) {
        personDetails.combined_credits.crew.forEach((credit) => {
            if (DROPDOWN_JOBS.includes(credit.job!)) {
                jobs.add(credit.job!);
            }
        });
    }

    return Array.from(jobs);
};

const People = () => {
    const [credits, setCredits] = useState<Array<any>>([]);
    const [jobOptions, setJobOptions] = useState<string[]>([]);
    const [selectedJob, setSelectedJob] = useState<string>('');
    setBackgroundImage('');
    const { id } = useParams();

    const getPersonDetails = async (id: string | undefined) => {
        const apiKey = process.env.REACT_APP_TMDB_API_KEY;
        const response = await fetch(`https://api.themoviedb.org/3/person/${id}?append_to_response=combined_credits&language=en-US`, {
            headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${apiKey}`,
            }
        });

        return await response.json();
    };

    const { data: personDetails, isLoading, error } = useQuery<PersonModel>({
        queryKey: ['person-details', id],
        queryFn: () => getPersonDetails(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    });

    const filterCredits = useCallback((job: string) => {
        if (!personDetails) return;
        let filteredCredits = [];
        if (job === 'Actor') {
            filteredCredits = getCredits(personDetails, 'cast');
        } else {
            filteredCredits = getCredits(personDetails, 'crew').filter((credit) => String(credit.job).toLowerCase() === job.toLowerCase());
        }
        setCredits(filteredCredits);
    }, [personDetails]);

    useEffect(() => {
        if (!personDetails) return;

        const options = extractJobOptions(personDetails);
        const defaultJob = mapDepartmentToJob(personDetails.known_for_department!);

        setJobOptions(options);
        setSelectedJob(options.includes(defaultJob) ? defaultJob : options[0]);
    }, [personDetails]);

    useEffect(() => {
        if (!personDetails || !selectedJob) return;
        filterCredits(selectedJob);
    }, [selectedJob, personDetails, filterCredits]);

    if (isLoading || !personDetails) return <Loader fullscreen message="Loading..." />;
    if (error) return <div>Error loading person details.</div>;

    const age = personDetails.birthday ? new Date(Date.now() - new Date(personDetails.birthday).getTime()).getUTCFullYear() - 1970 : null;
    let gender: string;
    if (personDetails.gender === Gender.Female) {
        gender = 'Female';
    } else if (personDetails.gender === Gender.Male) {
        gender = 'Male';
    } else if (personDetails.gender === Gender.NonBinary) {
        gender = 'Non-binary';
    } else {
        gender = '';
    }

    const formatBirthday = (date: string) => {
        if (!date) return 'Unknown';
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };

    const personInfoItems = [
        { label: 'Age', value: age },
        { label: 'Birthday', value: personDetails.birthday ? formatBirthday(personDetails.birthday) : null },
        { label: 'Birthplace', value: personDetails.place_of_birth },
        { label: 'Gender', value: gender },
        { label: 'Known For', value: personDetails.known_for_department },
    ];

    document.title = personDetails.name;

    return (
        <>
            <div className="person-details">
                {/* Person Overview */}
                <div className='overview-container'>
                    {personDetails.profile_path && (
                        <img className='profile-image' src={`https://image.tmdb.org/t/p/w300${personDetails.profile_path}`} alt={personDetails.name} />
                    )}
                    <div className='info-container'>
                        <h1>{personDetails.name}</h1>
                        <InfoList items={personInfoItems} />
                        <p className='biography'>{personDetails.biography}</p>
                    </div>
                </div>

                {/* Media Credits */}
                {credits.length > 0 && (
                    <div className='credits-container'>
                        <div className='credits-header'>
                            <h2 className='credits-title'>Credits</h2>
                            {/* Credits Filter */}
                            {jobOptions.length > 1 && (
                                <select value={selectedJob} onChange={(e) => setSelectedJob(e.target.value)}>
                                    {jobOptions.map((job) => (
                                        <option key={job} value={job}>{job}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <MediaList media={credits} showDetails={true} />
                    </div>
                )}
            </div>
        </>
    );
};

export default People;
